"use client";

import { useState } from "react";
import { ReactionPillStack, ReactionTooltipCard } from "@/components/ReactionPills";
import { useDismissable } from "@/lib/hooks/useDismissable";
import { useMessageReactors } from "@/lib/hooks/useConversation";
import { buildReactionTooltip } from "@/lib/reactions";
import type { MessageReaction } from "@/lib/api/messages";

/**
 * The reaction summary under a DM bubble — same stacked-emoji + total-count
 * look as components/ReactionSummary.tsx, but not that component: a DM
 * reaction is N per person and lives behind `/messages/{id}/reactions`, while
 * a post reaction is a singleton. Unlike ReactionSummary this also has to work
 * on a touch screen — DMs are read on mobile at least as often as desktop,
 * where hover never fires at all — so it opens on hover *or* tap, and a tap
 * stays open until dismissed (useDismissable) rather than closing the instant
 * the finger lifts.
 *
 * The `Reactor` rows it renders are the SAME shape `GET /posts/{id}/reactions`
 * returns (§2.10 #89), which is why the tooltip builder is shared.
 */
export function DmReactionSummary({
  counts,
  messageId,
}: {
  counts: MessageReaction[];
  /** A message id is the whole address now — the conversation is implied. */
  messageId: number;
}) {
  const [hovering, setHovering] = useState(false);
  const [tapOpen, setTapOpen] = useState(false);
  const visible = hovering || tapOpen;

  // Every hook above must run unconditionally, same reasoning as
  // ReactionSummary: bailing out before them would make the hook count
  // depend on `counts`.
  const entries = counts.filter((c) => c.count > 0);
  const total = entries.reduce((sum, c) => sum + c.count, 0);
  const topEntries = [...entries].sort((a, b) => b.count - a.count).slice(0, 3);

  // Fetched lazily — only once the summary is actually opened — and cached
  // by react-query, so re-opening the same message's tooltip a moment later
  // doesn't re-fetch.
  const { data } = useMessageReactors(messageId, visible);
  const reactors = data?.items ?? null;

  const ref = useDismissable<HTMLDivElement>(tapOpen, () => setTapOpen(false));

  if (entries.length === 0) return null;

  const tooltip = (() => {
    if (!visible || reactors === null) return null;

    // Shared with ReactionSummary so the two tooltips can't silently diverge
    // once a message has a lot of reactors.
    const { lines } = buildReactionTooltip(entries, reactors);

    // "og N andre" IS COUNTED FROM THE PILLS, not from the names that arrived.
    // `GET /messages/{id}/reactions` is cursor-paged (the legacy endpoint
    // shipped one unpaged array), and this tooltip does not page: it asks for
    // the route's maximum and stops. So on a message with more reactors than
    // one page holds, the builder's own overflow — which can only count what
    // it was handed — would say "og 10 andre" under a pill reading 35. The
    // `count` on each pill is the server's authoritative total; the difference
    // between it and the names actually listed is the only honest number here.
    const shown = lines.reduce((sum, line) => sum + line.names.length, 0);
    const overflow = Math.max(0, total - shown);

    return <ReactionTooltipCard anchorRef={ref} lines={lines} overflow={overflow} />;
  })();

  return (
    <div
      // One element, two jobs: what an outside click is measured against, and
      // what the tooltip is positioned against.
      ref={ref}
      className="inline-flex items-center gap-1.5"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {tooltip}
      <button
        type="button"
        onClick={() => setTapOpen((open) => !open)}
        aria-label="Se hvem der har reageret"
        className="flex items-center gap-1.5"
      >
        <ReactionPillStack entries={topEntries} total={total} />
      </button>
    </div>
  );
}
